import { Typography } from "@drpcrd/shared/ui";
import { MoreHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@drpcrd/utils/shared";

type Props = {
  title: string;
  description?: string | ReactNode[];
  leftElement?: ReactNode;
  bottomElement?: ReactNode;
  rightElement?: ReactNode;
  disabled?: boolean;
  className?: string;
};

/**
 * InfoPanel displays an icon, title, and optional description in a styled panel.
 */
export const InfoPanel = ({
  title,
  description,
  leftElement,
  bottomElement,
  rightElement,
  disabled,
  className,
}: Props) => {
  const isStringDescription = typeof description === "string";
  const isArrayDescription = Array.isArray(description);
  const displayItems = isArrayDescription ? description.slice(0, 5) : [];
  const hasMoreItems = isArrayDescription && description.length > 5;

  return (
    <div
      className={cn(
        "bg-bg-primary border-border-primary box-border flex w-full cursor-pointer flex-col items-center gap-2 rounded-lg border px-2 py-2",
        disabled &&
          "bg-bg-page text-text-disabled pointer-events-none border border-neutral-500 shadow-none",
        className,
      )}
    >
      {/* Upper Section */}
      <div className="flex w-full flex-row items-center justify-between gap-2">
        <div className="flex flex-row items-center gap-2">
          {/* Left Element */}
          {leftElement}

          {/* Labeling */}
          <div>
            <Typography
              weight="semibold"
              size="sm"
              color={disabled ? "disabled" : "primary"}
              className="leading-2.75"
            >
              {title}
            </Typography>
            {isStringDescription && (
              <Typography
                color={disabled ? "disabled" : "neutral"}
                truncate
                size="sm"
                className="leading-2.75"
              >
                {description}
              </Typography>
            )}
            {isArrayDescription && (
              <div className="mt-1.5 flex flex-row items-center gap-1.5">
                {displayItems}
                {hasMoreItems && <MoreHorizontal className="h-3 w-3" />}
              </div>
            )}
          </div>
        </div>

        {/* Right Element */}
        {rightElement && <div>{rightElement}</div>}
      </div>

      {/* Bottom Section */}
      {bottomElement && <div className="mr-auto">{bottomElement}</div>}
    </div>
  );
};
